import { API_BASE_URL as API_URL } from '../config/apiBase';

const CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const request = async (path, options = {}) => {
  const response = await fetch(`${API_URL}${path}`, { ...options, headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.message || 'Request failed');
  }
  return data.data;
};

let checkoutScriptPromise = null;

const loadCheckoutScript = () => {
  if (window.Razorpay) return Promise.resolve();
  if (!checkoutScriptPromise) {
    checkoutScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = CHECKOUT_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        checkoutScriptPromise = null;
        reject(new Error('Could not load Razorpay. Check your connection and try again.'));
      };
      document.body.appendChild(script);
    });
  }
  return checkoutScriptPromise;
};

export const billingService = {
  getConfig: () => request('/billing/config'),
  getMyBilling: () => request('/billing/me'),
  createOrder: ({ analyticsTier, products }) =>
    request('/billing/orders', {
      method: 'POST',
      body: JSON.stringify({ analyticsTier, products }),
    }),
  verifyPayment: (payload) =>
    request('/billing/verify', { method: 'POST', body: JSON.stringify(payload) }),
  reportFailure: (razorpayOrderId, reason) =>
    request(`/billing/orders/${encodeURIComponent(razorpayOrderId)}/failed`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }).catch(() => null),
  createQuoteRequest: (quote) =>
    request('/billing/quote-requests', { method: 'POST', body: JSON.stringify(quote) }),

  /**
   * Create an order on the server, open Razorpay Checkout, and verify the result.
   * Resolves with { status: 'paid', order } | { status: 'dismissed' }; rejects on failure.
   */
  checkout: async ({ analyticsTier, products }) => {
    const [order] = await Promise.all([
      billingService.createOrder({ analyticsTier, products }),
      loadCheckoutScript(),
    ]);

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        fn(value);
      };

      const razorpay = new window.Razorpay({
        key: order.keyId,
        order_id: order.razorpayOrderId,
        amount: order.amountPaise,
        currency: order.currency,
        name: 'AnalyticaX',
        description: 'AnalyticaX subscription (30 days)',
        prefill: order.prefill,
        theme: { color: '#146B57' },
        handler: async (response) => {
          try {
            const result = await billingService.verifyPayment(response);
            finish(resolve, { status: 'paid', order: result.order });
          } catch (error) {
            finish(reject, error);
          }
        },
        modal: {
          ondismiss: () => finish(resolve, { status: 'dismissed' }),
        },
      });

      razorpay.on('payment.failed', (response) => {
        const reason = response?.error?.description || 'Payment failed';
        billingService.reportFailure(order.razorpayOrderId, reason);
        // Razorpay keeps the modal open so the user can retry; only record the failure here.
      });

      razorpay.open();
    });
  },
};
